"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

import { trpc } from "@/lib/trpc/client";
import { getModuleIconForKey } from "@/lib/module-icons";
import {
  ACTIVE_STATES,
  EXCLUDED_MODULE_KEYS,
  MAIN_KEYS,
  humanizeModuleKey,
  normalizeModuleConfig,
} from "@/lib/module-config";

import { ToggleSwitch } from "./toggle-switch";

/**
 * Which areas appear in the member navigation.
 *
 * A second VIEW onto module config, not a second copy of it: every read and
 * write goes through the same `moduleConfig` procedures that /admin/customise
 * uses, so the two surfaces cannot disagree. Ordering, installing and removing
 * stay there — this is the on/off an owner reaches for most, next to the rest
 * of the arrangement.
 *
 * Saves immediately rather than joining the appearance SaveBar: these live in
 * StudioConfiguration, not StudioPreference, and one Save button writing two
 * stores would leave the settings half-applied if either call failed.
 *
 * Home and Browse are absent by construction — they are not modules, so there
 * is nothing to switch off and the "always on" rule needs no guard here.
 */
export function AreaToggles() {
  const router = useRouter();
  const utils = trpc.useUtils();
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const modules = trpc.moduleConfig.list.useQuery();
  const names = trpc.moduleConfig.definitionNames.useQuery();
  const allowed = trpc.moduleConfig.allowedKeys.useQuery();

  const reorderAll = trpc.moduleConfig.reorderAll.useMutation({
    onSuccess: async () => {
      await utils.moduleConfig.list.invalidate();
      // The member sidebar is server-rendered from the same config, so the
      // client cache invalidation above does not reach it. Without refresh()
      // the order is stored and the nav keeps showing the old one.
      router.refresh();
    },
    onError: () => toast.error("Could not reorder that area"),
    onSettled: () => setPendingKey(null),
  });

  const setHidden = trpc.moduleConfig.setHidden.useMutation({
    onSuccess: async () => {
      // The member nav reads the same config on the server, so refresh both
      // rather than leaving the sidebar showing the previous state.
      await utils.moduleConfig.list.invalidate();
      router.refresh();
      toast.success("Navigation updated for all studio members");
    },
    onError: () => toast.error("Could not update that area"),
    onSettled: () => setPendingKey(null),
  });

  if (modules.isLoading || names.isLoading || allowed.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading your areas…</p>;
  }

  if (modules.isError || !modules.data) {
    return (
      <p className="text-sm text-muted-foreground">
        Your areas could not be loaded. Reload the page to try again.
      </p>
    );
  }

  const allowedKeys = new Set(allowed.data ?? []);
  const definitionNames = new Map(Object.entries(names.data ?? {}));

  // Same normalisation and exclusions the nav itself applies, so this list is
  // exactly the set a member could see — but deliberately NOT splitModules,
  // which drops hidden entries. A settings list that hid the switched-off ones
  // would give an owner no way to switch them back on.
  const entries = Object.entries(normalizeModuleConfig(modules.data))
    .filter(
      ([key, entry]) =>
        ACTIVE_STATES.has(entry.state) &&
        !EXCLUDED_MODULE_KEYS.has(key) &&
        !MAIN_KEYS.has(key) &&
        allowedKeys.has(key),
    )
    .map(([key, entry]) => ({ key, ...entry }))
    .sort((a, b) => a.position - b.position);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This studio has no optional areas yet.
      </p>
    );
  }

  // Every ACTIVE module, in stored order. reorderAll re-indexes whatever it is
  // given, so it has to receive the complete active list — sending only the
  // rows shown here would leave the omitted ones re-indexed around them.
  const allActive = Object.entries(normalizeModuleConfig(modules.data))
    .filter(([, entry]) => ACTIVE_STATES.has(entry.state))
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([key]) => key);

  /**
   * Move a row past its nearest neighbour IN THIS LIST, not in the full active
   * one. The rows between them are settings-only modules an owner never sees
   * here, so stepping one position at a time would look like nothing happened.
   */
  const move = (key: string, direction: -1 | 1) => {
    const shownIndex = entries.findIndex((e) => e.key === key);
    const neighbour = entries[shownIndex + direction];
    if (!neighbour) return;

    const next = [...allActive];
    const from = next.indexOf(key);
    const to = next.indexOf(neighbour.key);
    if (from === -1 || to === -1) return;

    next.splice(from, 1);
    next.splice(to, 0, key);

    setPendingKey(key);
    reorderAll.mutate({ orderedKeys: next });
  };

  const isBusy = pendingKey !== null;

  return (
    <div className="flex flex-col gap-2">
      {entries.map((entry, index) => {
        const isShown = !entry.hidden;
        const name =
          definitionNames.get(entry.key) ?? humanizeModuleKey(entry.key);
        return (
          <ToggleSwitch
            key={entry.key}
            name={name}
            description={
              isShown
                ? "Shown in the navigation."
                : "Hidden from the navigation."
            }
            checked={isShown}
            disabled={isBusy}
            icon={
              // The nav's own icon for this area, at the row's size — the same
              // mark a member sees in the navigation this list controls.
              <span className="flex [&>svg]:h-[18px] [&>svg]:w-[18px]">
                {getModuleIconForKey(entry.key)}
              </span>
            }
            onToggle={() => {
              setPendingKey(entry.key);
              setHidden.mutate({ moduleKey: entry.key, hidden: isShown });
            }}
            onMove={(direction) => move(entry.key, direction)}
            canMoveUp={!isBusy && index > 0}
            canMoveDown={!isBusy && index < entries.length - 1}
          />
        );
      })}
    </div>
  );
}
